import { FC, useCallback, useState } from 'react'
import {
	Button,
	Checkbox,
	DatePicker,
	Divider,
	Input,
	InputNumber,
	Radio,
	Row,
	Select,
	Space,
	Switch,
	Tag,
	Typography,
	message,
} from 'antd'
import classNames from 'classnames'
import moment from 'moment'

import styles from 'container/ClientsPageLayout/styles.module.scss'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import {
	clearClientFilters,
	setClientActive,
	setClientFieldFilters,
	setClientInOnboarding,
	setClientOnBoardingStep,
	setClientPlan,
	setClientPlatformMode,
	setClientSuspended,
} from 'slices/client'
import {
	ClientFieldFilter,
	ClientFieldFilterOp,
	clientFilterFields,
	describeClientFieldFilter,
	clientFilterFieldMap,
} from 'data/clientFilterFields'

const { Text, Title } = Typography

const groupOrder = [
	'Identity',
	'Location',
	'Account',
	'Billing',
	'Dates',
	'WhatsApp',
	'Onboarding',
	'Marketing',
]

const ClientsFilterOverlay: FC = () => {
	const apiDetails = useAppSelector(state => state.client)
	const dispatch = useAppDispatch()
	const [field, setField] = useState<string>()
	const [op, setOp] = useState<ClientFieldFilterOp>('contains')
	const [value, setValue] = useState<string>()

	const selectedField = field ? clientFilterFieldMap[field] : undefined

	const setActive = useCallback(
		(next: boolean | undefined) => {
			dispatch(setClientActive(next))
		},
		[dispatch]
	)
	const setSuspended = useCallback(
		(next: boolean | undefined) => {
			dispatch(setClientSuspended(next))
		},
		[dispatch]
	)
	const setOnBoarding = useCallback(
		(next: boolean | undefined) => {
			dispatch(setClientInOnboarding(next))
		},
		[dispatch]
	)
	const setOnBoardingStep = useCallback(
		(next: number | undefined) => {
			dispatch(setClientOnBoardingStep(next))
		},
		[dispatch]
	)
	const setPlan = useCallback(
		(next: 'free' | 'paid' | undefined) => {
			dispatch(setClientPlan(next))
		},
		[dispatch]
	)
	const setPlatformMode = useCallback(
		(next: 'live' | 'demo' | undefined) => {
			dispatch(setClientPlatformMode(next))
		},
		[dispatch]
	)

	const chooseField = (nextField: string) => {
		const definition = clientFilterFieldMap[nextField]
		setField(nextField)
		setOp(definition?.type === 'string' ? 'contains' : 'eq')
		setValue(undefined)
	}

	const addFieldFilter = () => {
		if (!selectedField) {
			message.warning('Choose a field')
			return
		}
		const needsValue = op !== 'empty' && op !== 'notEmpty'
		const nextValue = value?.trim()
		if (needsValue && !nextValue) {
			message.warning('Enter a value')
			return
		}
		const nextFilter: ClientFieldFilter = {
			field: selectedField.field,
			op: selectedField.type === 'string' ? op : 'eq',
			...(needsValue ? { value: nextValue } : {}),
		}
		dispatch(
			setClientFieldFilters([
				...apiDetails.fieldFilters.filter(item => item.field !== nextFilter.field),
				nextFilter,
			])
		)
		setValue(undefined)
	}

	const removeFieldFilter = (fieldKey: string) => {
		dispatch(
			setClientFieldFilters(
				apiDetails.fieldFilters.filter(item => item.field !== fieldKey)
			)
		)
	}

	const valueControl = (() => {
		if (!selectedField || op === 'empty' || op === 'notEmpty') return null
		if (selectedField.type === 'boolean') {
			return (
				<Radio.Group value={value} onChange={event => setValue(event.target.value)}>
					<Radio.Button value='true'>Yes</Radio.Button>
					<Radio.Button value='false'>No</Radio.Button>
				</Radio.Group>
			)
		}
		if (selectedField.type === 'enum') {
			return (
				<Select
					showSearch
					optionFilterProp='children'
					placeholder='Choose a value'
					value={value}
					onChange={setValue}
					style={{ width: '100%' }}
				>
					{selectedField.options?.map(option => (
						<Select.Option key={option.value} value={option.value}>
							{option.label}
						</Select.Option>
					))}
				</Select>
			)
		}
		if (selectedField.type === 'number') {
			return (
				<InputNumber
					style={{ width: '100%' }}
					value={value === undefined || value === '' ? undefined : Number(value)}
					onChange={next => setValue(next === null ? undefined : String(next))}
				/>
			)
		}
		if (selectedField.type === 'date') {
			return (
				<DatePicker
					style={{ width: '100%' }}
					value={value ? moment(value, 'YYYY-MM-DD') : undefined}
					onChange={date => setValue(date ? date.format('YYYY-MM-DD') : undefined)}
				/>
			)
		}
		return (
			<Input
				placeholder='Value'
				value={value}
				onChange={event => setValue(event.target.value)}
				onPressEnter={addFieldFilter}
			/>
		)
	})()

	return (
		<div className={styles.overlay}>
			<Space direction='vertical' size='middle' style={{ width: '100%' }}>
				<Row justify='space-between' gutter={16}>
					<Checkbox
						checked={apiDetails.active !== undefined}
						onChange={() =>
							setActive(apiDetails.active === undefined ? true : undefined)
						}
					>
						<Text
							className={classNames({
								[styles.disabled]: apiDetails.active === undefined,
							})}
						>
							Active
						</Text>
					</Checkbox>
					<Switch
						checked={apiDetails.active}
						onChange={setActive}
						disabled={apiDetails.active === undefined}
					/>
				</Row>

				<Row justify='space-between' gutter={16}>
					<Checkbox
						checked={apiDetails.suspended !== undefined}
						onChange={() =>
							setSuspended(apiDetails.suspended === undefined ? true : undefined)
						}
					>
						<Text
							className={classNames({
								[styles.disabled]: apiDetails.suspended === undefined,
							})}
						>
							Suspended
						</Text>
					</Checkbox>
					<Switch
						checked={apiDetails.suspended}
						onChange={setSuspended}
						disabled={apiDetails.suspended === undefined}
					/>
				</Row>

				<Row justify='space-between' gutter={16}>
					<Checkbox
						checked={apiDetails.onBoarding !== undefined}
						onChange={() =>
							setOnBoarding(apiDetails.onBoarding === undefined ? true : undefined)
						}
					>
						<Text
							className={classNames({
								[styles.disabled]: apiDetails.onBoarding === undefined,
							})}
						>
							In Onboarding
						</Text>
					</Checkbox>
					<Switch
						checked={apiDetails.onBoarding}
						onChange={setOnBoarding}
						disabled={apiDetails.onBoarding === undefined}
					/>
				</Row>

				<Row justify='space-between' gutter={16} align='middle'>
					<Checkbox
						checked={apiDetails.onBoardingStep !== undefined}
						onChange={() =>
							setOnBoardingStep(
								apiDetails.onBoardingStep === undefined ? -2 : undefined
							)
						}
					>
						<Text
							className={classNames({
								[styles.disabled]: apiDetails.onBoardingStep === undefined,
							})}
						>
							Onboarding Step (-2 to 6)
						</Text>
					</Checkbox>
					<InputNumber
						className={styles.step}
						disabled={apiDetails.onBoardingStep === undefined}
						value={apiDetails.onBoardingStep ?? -2}
						onChange={next => setOnBoardingStep(next ?? undefined)}
						min={-2}
						max={6}
					/>
				</Row>
				<Row justify='space-between' gutter={16}>
					<Checkbox
						checked={apiDetails.plan !== undefined}
						onChange={() => {
							setPlan(apiDetails.plan === undefined ? 'free' : undefined)
						}}
					>
						<Text
							className={classNames({
								[styles.disabled]: apiDetails.plan === undefined,
							})}
						>
							Plan
						</Text>
					</Checkbox>
					<Radio.Group
						value={apiDetails.plan ?? 'free'}
						size='small'
						disabled={apiDetails.plan === undefined}
						onChange={event => {
							setPlan(event.target.value)
						}}
					>
						<Radio.Button value='free'>Free</Radio.Button>
						<Radio.Button value='paid'>Paid</Radio.Button>
					</Radio.Group>
				</Row>
				<Row justify='space-between' gutter={16}>
					<Checkbox
						checked={apiDetails.platformMode !== undefined}
						onChange={() => {
							setPlatformMode(
								apiDetails.platformMode === undefined ? 'demo' : undefined
							)
						}}
					>
						<Text
							className={classNames({
								[styles.disabled]: apiDetails.platformMode === undefined,
							})}
						>
							Mode
						</Text>
					</Checkbox>
					<Radio.Group
						value={apiDetails.platformMode ?? 'demo'}
						size='small'
						disabled={apiDetails.platformMode === undefined}
						onChange={event => {
							setPlatformMode(event.target.value)
						}}
					>
						<Radio.Button value='demo'>Demo</Radio.Button>
						<Radio.Button value='live'>Live</Radio.Button>
					</Radio.Group>
				</Row>

				<Divider style={{ margin: '4px 0' }} />
				<Title level={5} style={{ marginBottom: 0 }}>
					Any field
				</Title>
				<Text type='secondary'>
					Filter by almost any client field. API keys and tokens are not included.
				</Text>
				<Select
					showSearch
					optionFilterProp='children'
					placeholder='Choose a field'
					value={field}
					onChange={chooseField}
					style={{ width: '100%' }}
				>
					{groupOrder.map(group => (
						<Select.OptGroup key={group} label={group}>
							{clientFilterFields
								.filter(item => item.group === group)
								.map(item => (
									<Select.Option key={item.field} value={item.field}>
										{item.label}
									</Select.Option>
								))}
						</Select.OptGroup>
					))}
				</Select>
				{selectedField?.type === 'string' && (
					<Select value={op} onChange={setOp} style={{ width: '100%' }}>
						<Select.Option value='contains'>Contains</Select.Option>
						<Select.Option value='eq'>Is</Select.Option>
						<Select.Option value='empty'>Is empty</Select.Option>
						<Select.Option value='notEmpty'>Is set</Select.Option>
					</Select>
				)}
				{valueControl}
				<Button type='primary' onClick={addFieldFilter} block>
					Add field filter
				</Button>
				{apiDetails.fieldFilters.length > 0 && (
					<Space size={[8, 8]} wrap>
						{apiDetails.fieldFilters.map(filter => (
							<Tag
								key={filter.field}
								closable
								onClose={event => {
									event.preventDefault()
									removeFieldFilter(filter.field)
								}}
							>
								{describeClientFieldFilter(filter)}
							</Tag>
						))}
					</Space>
				)}

				<Button type='default' onClick={() => dispatch(clearClientFilters())} block>
					Clear Filters
				</Button>
			</Space>
		</div>
	)
}

export default ClientsFilterOverlay
